import { ArrowLeft, Mail, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { forgotApi } from "../../api/authAPI";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import logger from "../../utils/logger";
import React from "react";

type ForgotFormInputs = {
  username: string;
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotFormInputs>();
  const [loading, setLoading] = React.useState(false);

  const onSubmit = async (data: ForgotFormInputs) => {
    try {
      setLoading(true);
      let res = await forgotApi({ email: data.username });
      let resData = res?.data;
      if (resData?.status) {
        toast.success(resData.message || "OTP sent successfully");
        navigate("/otp-verification?", {
          state: {
            type: "forgot",
            dialCode: resData.dialCode,
            phone: resData.phone,
            cooldownSeconds: resData.cooldownSeconds,
          },
        });
      } else {
        toast.error(resData?.message || "Failed to send OTP");
      }
    } catch (error: any) {
      logger.error("forgot-error", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to send OTP",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="bg-white/10 backdrop-blur-sm rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Forgot Password</h1>
        <p className="text-blue-100">
          Enter your username or email to reset your password
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Username / Email */}
        <div>
          <p className="block text-sm font-medium text-blue-100 mb-2">
            Username or Email *
          </p>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
            <input
              type="text"
              placeholder="Enter username or email"
              className={`w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all ${
                errors.username ? "border-red-400" : "border-white/20"
              }`}
              {...register("username", {
                required: "Username or email is required",
                validate: (value) => {
                  const trimmedValue = value.trim();

                  // Email regex
                  const emailRegex =
                    /^(?!\.)(?!.*\.\.)[a-zA-Z0-9._%+-]+(?<!\.)@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                  const usernameRegex = /^\w{3,20}$/;
                  const isEmail = trimmedValue.includes("@");
                  const isValid = isEmail
                    ? emailRegex.test(trimmedValue)
                    : usernameRegex.test(trimmedValue);

                  return (
                    isValid || "Please enter a valid username or email address"
                  );
                },
              })}
            />
          </div>
          {errors.username && (
            <p className="mt-1 text-sm text-red-300">
              {errors.username.message}
            </p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-white text-slate-800 font-semibold py-3 px-6 rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
        >
          {loading ? "Sending OTP..." : "Send OTP"}
        </button>

        {/* Back to login */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center text-blue-100 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </button>
        </div>
      </form>
    </div>
  );
};

export default ForgotPassword;
